import { IntlShape } from 'react-intl';

import { FieldCatalog } from './conditionForm';
import { dynamicApproverLabel } from './dynamicApproverLabels';
import messages from './messages';
import {
  ATTRIBUTE_SCOPES,
  dynamicPickerValue,
  DynamicApproverRow,
  dynamicRow,
  isDynamicApprover,
} from './ruleForm';
import type { SearchableMultiSelectOption } from './SearchableMultiSelect';
import { ApprovalRule } from './types';

/**
 * The key the catalog lists the Opening Owner condition attribute under
 * (`Approvals::AttributeRegistry::OPENING_OWNER`). It holds a user id, not an
 * email address, and the Opening owner approver is offered on its own.
 */
const OPENING_OWNER_ATTRIBUTE = 'opening_owner';


/** The account's attributes by key, with their names and value types. */
const catalogAttributes = (catalog?: FieldCatalog) =>
  new Map(
    (catalog?.subjects ?? [])
      .flatMap(subject => subject.attributes ?? [])
      .filter(attribute => attribute.key)
      .map(attribute => [attribute.key as string, attribute]),
  );

/**
 * What the approver and backup pickers offer besides people: the Opening
 * owner, then one entry per text attribute in each scope that carries it,
 * "HRBP Email (Opening)".
 *
 * An attribute qualifies when every value it holds is one or more email
 * addresses (the catalog's `email_valued_attributes`). Those come first, by
 * scope and then by name. The scopes' other text attributes follow, disabled,
 * with the reason under their name. Number, date and boolean attributes never
 * hold an address, so they are not offered.
 */
export const dynamicApproverOptions = (
  intl: IntlShape,
  catalog?: FieldCatalog,
): SearchableMultiSelectOption[] => {
  const attributes = catalogAttributes(catalog);
  const emailValued = new Set(catalog?.emailValuedAttributes ?? []);

  const attributeOptions = ATTRIBUTE_SCOPES.flatMap(scope =>
    (
      catalog?.sourcedAttributes.find(entry => entry.scope === scope)
        ?.attributes ?? []
    )
      .filter(key => key !== OPENING_OWNER_ATTRIBUTE)
      .map(key => ({ key, attribute: attributes.get(key) }))
      // Text only: the server reads an `any` attribute as `string`
      // (`Approvals::ValueType.normalize`), and only text holds an address.
      .filter(({ attribute }) => attribute?.type === 'string')
      .map(({ key, attribute }) => {
        const row: DynamicApproverRow = {
          approver_type: 'attribute',
          attribute_key: key,
          attribute_scope: scope,
        };

        return {
          name: attribute?.label ?? key,
          qualifies: emailValued.has(key),
          option: {
            value: dynamicPickerValue(row),
            label: dynamicApproverLabel(intl, row, attribute?.label),
            disabledReason: emailValued.has(key)
              ? undefined
              : intl.formatMessage(messages.attributeNotEmailValued),
          },
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name)),
  );

  return [
    {
      value: dynamicPickerValue({ approver_type: 'opening_owner' }),
      label: dynamicApproverLabel(intl, { approver_type: 'opening_owner' }),
    },
    ...attributeOptions.filter(entry => entry.qualifies).map(entry => entry.option),
    ...attributeOptions.filter(entry => !entry.qualifies).map(entry => entry.option),
  ];
};

/**
 * The chip label for every dynamic row the rule stores, by picker value, so a
 * saved row the pickers do not offer still reads by name: the flag is off, or
 * the attribute stopped qualifying. The attribute's name comes from the rule
 * (`attribute_label`), and from the catalog when the rule has none.
 */
export const heldDynamicLabels = (
  intl: IntlShape,
  rule: ApprovalRule | undefined,
  catalog?: FieldCatalog,
): Record<string, string> => {
  const attributes = catalogAttributes(catalog);

  return Object.fromEntries(
    (rule?.levels ?? [])
      .flatMap(level => [...(level.approvers ?? []), ...(level.backup_approvers ?? [])])
      .filter(isDynamicApprover)
      .map(approver => {
        const row = dynamicRow(approver);
        const name =
          approver.attribute_label ??
          attributes.get(approver.attribute_key ?? '')?.label;

        return [dynamicPickerValue(row), dynamicApproverLabel(intl, row, name)];
      }),
  );
};
